// Curated revenue plays for outdoor blinds / small trade retail.
// Hand-picked, industry-tested. Zero LLM cost — pure static data.
// Categories, effort, time-to-impl and $ estimates based on typical AU pricing.

export const GROW_CATEGORIES = [
  { id: "quick-wins", label: "Quick Wins", icon: "⚡", desc: "Do this week. Zero setup." },
  { id: "recurring", label: "Recurring Revenue", icon: "🔁", desc: "Turn one-off installs into forever customers." },
  { id: "upsell", label: "Cross-Sell & Upsell", icon: "📈", desc: "Extra $$ from customers you already have." },
  { id: "b2b", label: "New B2B Markets", icon: "🏢", desc: "Bulk orders. Repeat business. Bigger cheques." },
  { id: "products", label: "New Products", icon: "🆕", desc: "Range extensions that print money." },
  { id: "seasonal", label: "Seasonal Plays", icon: "☀️", desc: "Time-locked campaigns that convert fast." },
  { id: "marketing", label: "Marketing & Referrals", icon: "📣", desc: "Low-cost lead machines." },
  { id: "cashflow", label: "Cashflow Boosts", icon: "💰", desc: "Same revenue. Faster money in." },
];

export const GROW_IDEAS = [
  // ============ QUICK WINS ============
  {
    id: "cash-discount",
    category: "quick-wins",
    title: "Offer a 5% cash-on-install discount",
    desc: "Add a 5% discount if the client pays 100% on install day. Kills 30-60 day debtors overnight and dramatically improves cashflow.",
    revenue: "Saves ~$3,000/mo in chasing time · Reduces overdue by 60%+",
    effort: "low",
    time: "this-week",
    steps: [
      "Add a line to your quotes: 'Pay on install day = 5% off total.'",
      "Print it on the invoice, mention it verbally at the sale.",
      "Track uptake in Billy — you'll see overdue receivables drop within 30 days.",
    ],
  },
  {
    id: "google-post-every-install",
    category: "quick-wins",
    title: "Post every install to Google Business Profile",
    desc: "Google prioritises businesses that post regularly. Every install = free photo + short caption. Ranks you higher for 'blinds near me' searches.",
    revenue: "2-5 extra leads/mo, ~$4-10k extra revenue",
    effort: "low",
    time: "this-week",
    steps: [
      "Take 2 photos of every install: before + after.",
      "Post to Google Business the same day with location + product name.",
      "Do it every install for 90 days — you'll rank in the top 3 locally.",
    ],
  },
  {
    id: "raise-prices-jan-1",
    category: "quick-wins",
    title: "Lock in an automatic 4-5% price rise every 1 January",
    desc: "Every year on Jan 1, prices go up 4-5%. Customers expect it — inflation is real. Most tradies forget. This alone lifts annual profit by low-thousands.",
    revenue: "+$5,000-15,000/yr on same volume",
    effort: "low",
    time: "this-week",
    steps: [
      "Set a calendar reminder for every 15 December.",
      "Update your price sheet by 4-5% (or CPI + 1%).",
      "Send an email to trade customers 2 weeks before: 'Prices rise Jan 1, order before to lock old rate.'",
    ],
  },
  {
    id: "ask-for-reviews",
    category: "quick-wins",
    title: "Automate a review-request SMS 24hrs after install",
    desc: "Google reviews are the #1 driver of local lead volume. Most customers happily leave a review — they just need a nudge. One SMS 24hrs after install with the review link.",
    revenue: "10× your reviews → 2-3× your leads",
    effort: "low",
    time: "this-week",
    steps: [
      "Get your Google review link from your Business Profile.",
      "Save an SMS template: 'Hi [name], hope you love the new blinds! If you have 30 seconds, a Google review means the world to us: [link] — Craig.'",
      "Send it manually after every install (or use Google's automated SMS after ~5 reviews to test).",
    ],
  },

  // ============ RECURRING REVENUE ============
  {
    id: "annual-service-plan",
    category: "recurring",
    title: "Sell annual 'Clean & Service' contracts",
    desc: "$150-250/yr per install for a yearly service: motor check, fabric clean, track lubricate, warranty audit. Recurring revenue you don't have to re-sell every year.",
    revenue: "$150-250 × 200 installs = $30-50k/yr recurring",
    effort: "med",
    time: "this-month",
    steps: [
      "Offer it as a 3-pack at sale time ($400 for 3 years — locks in loyalty).",
      "Add existing customers back via email: '$149 first-year service, book before June.'",
      "Set service reminders in your calendar (Billy calendar can hold these).",
    ],
  },
  {
    id: "motor-warranty-extension",
    category: "recurring",
    title: "Sell extended motor warranties",
    desc: "Somfy/Elero motors carry 5-yr manufacturer warranty. Extend to 10 years for $200-300 upfront. Pure margin — you're just underwriting.",
    revenue: "$200 × 30 installs/mo × 60% take-up = $3,600/mo extra",
    effort: "low",
    time: "this-month",
    steps: [
      "Talk to your supplier about their extended warranty program (Somfy has one).",
      "Add it as a $299 upsell line at quote time.",
      "Print terms clearly — 'peace of mind for 10 years' beats 'we'll cover it, mate' every time.",
    ],
  },
  {
    id: "loyalty-replacement-discount",
    category: "recurring",
    title: "10% loyalty discount on replacements after 7 years",
    desc: "Track every install date. At year 7, send an email/SMS: 'Time for an upgrade? 10% off replacements for our loyal customers.' Locks them in for their next cycle.",
    revenue: "Doubles replacement conversion — worth $20-40k/yr",
    effort: "med",
    time: "3-months",
    steps: [
      "Tag every bill/income in Billy with a Job ID.",
      "Export your customer list annually, sort by install date.",
      "Send a bulk email to anyone at 7+ years with the offer.",
    ],
  },

  // ============ CROSS-SELL & UPSELL ============
  {
    id: "motorisation-retrofit",
    category: "upsell",
    title: "Retrofit motors onto old manual awnings",
    desc: "Every manual awning from the last 15 years can be motorised for $600-1,200 per unit. Half your existing customer base is a candidate. Somfy's retrofit kits make this straightforward.",
    revenue: "$800 avg × 30 conversions = $24k in a season",
    effort: "med",
    time: "this-month",
    steps: [
      "Email your existing customer database with a photo + '$799 motor retrofit — say goodbye to the crank handle.'",
      "Offer a $50 site inspection (credited if they proceed).",
      "Bundle with smart-home add-on for +$300.",
    ],
  },
  {
    id: "smart-home-integration",
    category: "upsell",
    title: "Smart-home integration (Alexa / Google / Somfy TaHoma)",
    desc: "Motorised blinds + Somfy TaHoma bridge = voice/timer control. $400-600 add-on with 80%+ margin. Especially appeals to younger homeowners and new builds.",
    revenue: "$500 × 20 installs/mo = $10k/mo",
    effort: "low",
    time: "this-month",
    steps: [
      "Sit down with the local Somfy rep for a 1-hour training.",
      "Add it to every motorised quote as an optional line.",
      "Have a live demo unit in your showroom.",
    ],
  },
  {
    id: "insect-screens-addon",
    category: "upsell",
    title: "Sell insect screens as an add-on",
    desc: "Every outdoor room needs bug screens. Retractable or fixed. High margin, easy install, complementary product. Bundle with any new pergola/patio blind quote.",
    revenue: "$800-2,000 per install as an add-on",
    effort: "med",
    time: "this-month",
    steps: [
      "Partner with a local screen supplier (Centor, Freedom Screens).",
      "Bundle: 'Get the flies out — screens installed with your blinds, save $200.'",
      "Focus on Nov-Feb Aussie fly season.",
    ],
  },
  {
    id: "led-lighting-strips",
    category: "upsell",
    title: "LED strip lighting inside pergola blinds",
    desc: "Discreet LED strips along the top rail of pergola blinds — turns the outdoor room into a nightspot. $400-700 add-on, sub-$100 in materials.",
    revenue: "$500 × 15/mo = $7,500/mo",
    effort: "low",
    time: "this-month",
    steps: [
      "Stock warm-white waterproof LED strip + a plug-in dimmer.",
      "Show it lit up in your showroom.",
      "Sell as 'ambience upgrade — you'll use the space every night, not just weekends.'",
    ],
  },

  // ============ NEW B2B MARKETS ============
  {
    id: "hospitality-outdoor-screens",
    category: "b2b",
    title: "Café & restaurant outdoor dining screens",
    desc: "Post-COVID, every café expanded outdoors. They need windproof screens, café blinds and heaters. One good pub contract = 20 residential installs in revenue. Repeat business every 3-5 years.",
    revenue: "$15-40k per venue · 3-5 year replacement cycle",
    effort: "med",
    time: "3-months",
    steps: [
      "Cold-walk every cafe/pub within 20km with a 1-page 'outdoor dining solutions' flyer.",
      "Offer a free site measure + 3D visualisation.",
      "Reference existing hospitality clients (photos + written quote).",
    ],
  },
  {
    id: "body-corporate-strata",
    category: "b2b",
    title: "Body corporate / strata contracts (apartments)",
    desc: "Apartment complexes often need entire building fit-outs of privacy blinds/screens. Body corp pays, single point of contact, 40-100+ units. Requires quotes + insurance but the volume is huge.",
    revenue: "$40-150k per complex",
    effort: "high",
    time: "3-months",
    steps: [
      "Get $10M public liability + $5M product liability insurance sorted.",
      "Contact strata managers (Strata Community Association has a member directory).",
      "Do one small building well → they refer you to the next 5.",
    ],
  },
  {
    id: "real-estate-staging",
    category: "b2b",
    title: "Real estate agent partnership — staging blinds",
    desc: "Empty houses look terrible without window furnishings. Partner with a local agent: cheap temporary blinds for staging, first-right-of-refusal when new owner buys.",
    revenue: "10-15 leads/mo from one agency",
    effort: "med",
    time: "this-month",
    steps: [
      "Approach 2-3 top-selling agents in your area with a proposal.",
      "Offer a 20% agent commission on any sale that comes through.",
      "Set up an 'in-progress' referral tag in Billy so you track ROI.",
    ],
  },
  {
    id: "insurance-repair-panel",
    category: "b2b",
    title: "Join an insurance repair panel (storm damage)",
    desc: "Storm-damaged awnings need urgent replacement. Insurers pay retail rates, immediate approval, guaranteed payment in 14 days. Sign up as an approved repairer with 2-3 major insurers.",
    revenue: "10-20 storm jobs/yr @ $3-8k each = $50-120k",
    effort: "med",
    time: "3-months",
    steps: [
      "Apply to be a preferred repairer with IAG, Suncorp, Allianz.",
      "Get your qualifications + insurance certs in order.",
      "Do one job perfectly — they'll flood you with work.",
    ],
  },
  {
    id: "aged-care-schools",
    category: "b2b",
    title: "Aged care & schools — shade sails and privacy screens",
    desc: "Institutional buyers with capex budgets. Long approval cycles but tickets are $30-80k per site. Aged care facilities need weather protection on balconies; schools need shade sails.",
    revenue: "$40-80k per site",
    effort: "high",
    time: "3-months",
    steps: [
      "Get on the AusTender government supplier register.",
      "Cold-email facility managers of the 20 largest aged care homes in your region.",
      "Partner with a local architect who works in that sector.",
    ],
  },

  // ============ NEW PRODUCTS ============
  {
    id: "ziptrak-clear-pvc",
    category: "products",
    title: "Ziptrak / Cafe Blinds (retractable clear PVC)",
    desc: "The fastest-growing product in the industry. Retractable clear PVC weather screens. 60-70% margin. If you're not selling these, competitors are.",
    revenue: "$3-8k per install · 8-12 installs/mo",
    effort: "med",
    time: "this-month",
    steps: [
      "Become a Ziptrak or similar authorised installer.",
      "Feature it prominently on your website + Google Business photos.",
      "Bundle with your existing outdoor blinds range.",
    ],
  },
  {
    id: "louvered-roofs",
    category: "products",
    title: "Louvred roof systems (Vergola-style)",
    desc: "$15-35k per install — motorised aluminium louvre roofs. Massive margin, huge premium market. Popular with high-end residential and commercial. Requires structural know-how but transforms your business.",
    revenue: "$25k avg × 2/mo = $50k/mo",
    effort: "high",
    time: "3-months",
    steps: [
      "Partner with a local structural engineer or become certified.",
      "Get sample photos + a display unit.",
      "Target renovators and new builds — architects specify these.",
    ],
  },
  {
    id: "roller-shutters",
    category: "products",
    title: "Aluminium roller shutters (security + insulation)",
    desc: "Complementary to outdoor blinds. Insulation, security, blackout. Popular in bushfire zones and coastal regions. $2-6k per window.",
    revenue: "$4k avg × 6/mo = $24k/mo",
    effort: "med",
    time: "3-months",
    steps: [
      "Partner with a supplier like OzRoll or SunSaver.",
      "Cross-sell to existing customers — 'complete the outdoor room.'",
      "Target bushfire-prone regions specifically.",
    ],
  },

  // ============ SEASONAL PLAYS ============
  {
    id: "storm-prep-campaign",
    category: "seasonal",
    title: "October storm-prep campaign",
    desc: "Every October (start of AU storm season), run a 'Storm-ready your outdoor blinds' campaign. Service checks, secure mounts, sell wind sensors. Prevents damage claims and generates $200-400 per service call.",
    revenue: "80-120 service calls in Sept-Oct",
    effort: "low",
    time: "seasonal",
    steps: [
      "Email/SMS every past customer in mid-September.",
      "Offer a $199 pre-storm inspection + wind sensor upgrade.",
      "Free hail cover as a bundle option.",
    ],
  },
  {
    id: "end-of-summer-clearance",
    category: "seasonal",
    title: "March end-of-summer clearance",
    desc: "March-April is the second-best sales window (after Sept-Nov). People want outdoor rooms sorted before winter. 'End of season, last chance to enjoy your summer for 6 months' converts hard.",
    revenue: "20-30% sales lift in Mar-Apr",
    effort: "low",
    time: "seasonal",
    steps: [
      "Discount slow-moving stock 15%.",
      "Run a 2-week Facebook campaign geo-targeted 30km radius.",
      "Push motorisation upgrades ('use them more with a remote').",
    ],
  },
  {
    id: "christmas-gift-vouchers",
    category: "seasonal",
    title: "Christmas gift vouchers ($100-500)",
    desc: "In the last week of November, promote gift vouchers 'towards new outdoor blinds'. Kids/spouses/parents buy them for renovators. Breakage rate (unredeemed) is 15-25% — pure profit.",
    revenue: "$5-10k in Dec + follow-up installs",
    effort: "low",
    time: "seasonal",
    steps: [
      "Design a simple PDF voucher template.",
      "Sell via Facebook + email database from ~15 November.",
      "Track redemption in Billy with a Voucher tag.",
    ],
  },

  // ============ MARKETING & REFERRALS ============
  {
    id: "referral-program-formal",
    category: "marketing",
    title: "$150 formal referral program",
    desc: "Give every customer a $150 credit when they refer someone who buys. Print business-card-sized flyers, include one with every install. Referrals convert at 30-50% (vs 5-10% for cold ads).",
    revenue: "3-5 referrals/mo × $4k avg install = $12-20k/mo",
    effort: "low",
    time: "this-week",
    steps: [
      "Print 500 referral cards ($100 at Vistaprint).",
      "Add one to every install pack with a QR to your quote form.",
      "Set a 'REFERRAL' tag in Billy so you track ROI.",
    ],
  },
  {
    id: "facebook-local-groups",
    category: "marketing",
    title: "Post in local Facebook groups weekly (not ads)",
    desc: "Every suburb has a 'Bayside Local Business' or 'North Shore Renovations' Facebook group. Post before/afters weekly. Don't advertise — share transformation stories. Leads flow in for free.",
    revenue: "3-8 warm leads/mo",
    effort: "low",
    time: "this-week",
    steps: [
      "Join 10-15 local suburb groups.",
      "Post one great before/after per week per group (rotate).",
      "Never lead with price — lead with the story.",
    ],
  },
  {
    id: "google-ads-competitor-terms",
    category: "marketing",
    title: "Google Ads on competitor brand names",
    desc: "Bid on 'competitor name blinds' in Google Ads. When someone searches your competitor, your ad shows first with a 'get a better quote' hook. Cheap, effective, immediate.",
    revenue: "Depends on competitor volume — often 3-5× ROAS",
    effort: "med",
    time: "this-month",
    steps: [
      "List your top 3 competitors' business names.",
      "Set up a Google Ads campaign bidding on those terms + 'blinds [suburb]'.",
      "Landing page: '3 quotes vs 1. Get a better one from Craig at Outdoor Blind Co.'",
    ],
  },

  // ============ CASHFLOW BOOSTS ============
  {
    id: "increase-deposit-percentage",
    category: "cashflow",
    title: "Increase deposit from 50% to 60% or 70%",
    desc: "Standard install-industry deposit is 50%. Bumping to 60-70% dramatically improves cashflow without hurting close rate. Customers rarely push back — they want the job done.",
    revenue: "Frees up $30-50k in working capital",
    effort: "low",
    time: "this-week",
    steps: [
      "Update your quote template: 'Deposit: 60% on order · 30% on pre-install site check · 10% on install.'",
      "Test on next 10 quotes — measure close rate change.",
      "Roll out permanently if close rate doesn't drop.",
    ],
  },
  {
    id: "public-pay-links-sms",
    category: "cashflow",
    title: "SMS every invoice with a public pay-link",
    desc: "Billy already has a `/pay/:id` link per bill. SMS the link with every invoice. One tap → they pay. Reduces average collection time from 30 days to 3.",
    revenue: "Free — saves 15+ hrs/mo of chasing",
    effort: "low",
    time: "this-week",
    steps: [
      "On the Bill detail page, copy the public pay link.",
      "SMS the customer: 'Hi Sarah, invoice is ready — 1-tap pay: [link]'",
      "Watch your overdue debtors drop 60-80%.",
    ],
  },
  {
    id: "trade-account-supplier-terms",
    category: "cashflow",
    title: "Negotiate 60-day supplier terms",
    desc: "Most suppliers (fabric, motors, tracks) offer 30-day terms as standard. Ask for 60-day. If you install in 14 days and get 50% deposit up front, 60-day supplier terms means the customer literally funds the entire job.",
    revenue: "Eliminates working-capital squeeze",
    effort: "low",
    time: "this-month",
    steps: [
      "Ring your top 3 suppliers.",
      "Say: 'We're growing 30% this year — can you extend our terms to 60 days?'",
      "Most say yes to good-paying customers.",
    ],
  },
];
